import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  query,
  where,      
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";

import {
  useNavigate,
} from "react-router-dom";

import {
  db,
} from "../firebase";

import "./Notifications.css";


/* =========================================================
   NOTIFICATION CENTRE
========================================================= */

const Notifications = () => {
  const navigate = useNavigate();

  const username = localStorage.getItem("username");

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("markets");


  /* =======================================================
     LOAD NOTIFICATIONS
  ======================================================= */

  useEffect(() => {
    if (!username) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadNotifications = async () => {
      try {
        setLoading(true);

        const notificationsQuery = query(
          collection(db, "notifications"),
          where("username", "==", username)
        );

        const snap = await getDocs(notificationsQuery);

        if (cancelled) return;

        const list = snap.docs.map((notificationDoc) => {
          const data = notificationDoc.data();

          return {
            id: notificationDoc.id,
            ...data,
          };
        });

        setNotifications(list);

        /*
          Mark unread notifications as read.
        */

        const unreadDocs = snap.docs.filter(
          (notificationDoc) =>
            notificationDoc.data()?.read !== true
        );

        if (unreadDocs.length > 0) {
          await Promise.all(
            unreadDocs.map((notificationDoc) =>
              updateDoc(
                doc(
                  db,
                  "notifications",
                  notificationDoc.id
                ),
                {
                  read: true,
                }
              )
            )
          );
        }
      } catch (error) {
        console.error(
          "Notifications load error:",
          error
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [username]);


  /* =======================================================
     NORMALIZE CATEGORY
  ======================================================= */

  const getCategory = (notification) => {
    const rawCategory =
      notification?.category ||
      notification?.type ||
      notification?.section ||
      "";

    const categoryValue = String(rawCategory)
      .trim()
      .toLowerCase();

    /*
      Transaction notifications will appear
      inside the Account tab.
    */

    if (
      categoryValue.includes("transaction") ||
      categoryValue.includes("deposit") ||
      categoryValue.includes("withdraw") ||
      categoryValue.includes("account")
    ) {
      return "account";
    }

    if (categoryValue.includes("market")) {
      return "markets";
    }

    if (categoryValue.includes("message")) {
      return "messages";
    }

    if (
      categoryValue.includes("news") ||
      categoryValue.includes("hf")
    ) {
      return "news";
    }

    /*
      Automatic fallback based on title.
    */

    const title = String(
      notification?.title || ""
    ).toLowerCase();

    if (
      title.includes("market") ||
      title.includes("alert") ||
      title.includes("symbol")
    ) {
      return "markets";
    }

    if (
      title.includes("approved") ||
      title.includes("account") ||
      title.includes("deposit") ||
      title.includes("withdraw") ||
      title.includes("transaction")
    ) {
      return "account";
    }

    if (
      title.includes("news") ||
      title.includes("bfc")
    ) {
      return "news";
    }

    return "messages";
  };


  /* =======================================================
     GET CREATED TIME
  ======================================================= */

  const getCreatedTime = (notification) => {
    const createdAt = notification?.createdAt;

    if (createdAt?.toDate) {
      return createdAt.toDate().getTime();
    }

    if (createdAt?.seconds) {
      return Number(createdAt.seconds) * 1000;
    }

    if (createdAt instanceof Date) {
      return createdAt.getTime();
    }

    const parsedDate = new Date(createdAt).getTime();

    return Number.isFinite(parsedDate)
      ? parsedDate
      : 0;
  };


  /* =======================================================
     TAB COUNTS
  ======================================================= */

  const tabCounts = useMemo(() => {
    const counts = {
      markets: 0,
      messages: 0,
      account: 0,
      news: 0,
    };

    notifications.forEach((notification) => {
      const category = getCategory(notification);

      if (counts[category] !== undefined) {
        if (notification.read !== true) {
          counts[category] += 1;
        }
      }
    });

    return counts;
  }, [notifications]);


  /* =======================================================
     FILTER CURRENT TAB
  ======================================================= */

  const filteredNotifications = useMemo(() => {
    return [...notifications]
      .filter(
        (notification) =>
          getCategory(notification) === activeTab
      )
      .sort(
        (firstNotification, secondNotification) =>
          getCreatedTime(secondNotification) -
          getCreatedTime(firstNotification)
      );
  }, [
    notifications,
    activeTab,
  ]);


  /* =======================================================
     FORMAT DATE
  ======================================================= */

  const formatDate = (notification) => {
    const timestamp = getCreatedTime(notification);

    if (!timestamp) {
      return "";
    }

    const date = new Date(timestamp);

    const parts = new Intl.DateTimeFormat(
      "en-GB",
      {
        timeZone: "Africa/Dar_es_Salaam",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }
    ).formatToParts(date);

    const getPart = (type) =>
      parts.find(
        (part) => part.type === type
      )?.value || "";

    return (
      `${getPart("day")}/ `+
      `${getPart("month")}/ `+
      `${getPart("year")} ` +
      `${getPart("hour")}: `+
      `${getPart("minute")}: `+
      `${getPart("second")}`
    );
  };


  /* =======================================================
     GET TITLE
  ======================================================= */

  const getTitle = (notification) => {
    return (
      notification?.title ||
      "Notification"
    );
  };


  /* =======================================================
     GET MESSAGE
  ======================================================= */

  const getMessage = (notification) => {
    return (
      notification?.message ||
      notification?.body ||
      ""
    );
  };


  /* =======================================================
     GET ACTION TEXT
  ======================================================= */

  const getActionText = (notification) => {
    if (notification?.actionText) {
      return notification.actionText;
    }

    const category = getCategory(notification);

    if (category === "markets") {
      return "View Symbol";
    }

    if (category === "account") {
      return "Proceed";
    }

    if (category === "news") {
      return "View";
    }

    return "";
  };


  /* =======================================================
     HANDLE ACTION
  ======================================================= */

  const handleAction = (notification) => {
    /*
      If admin saved an action URL,
      use that URL first.
    */

    if (notification?.actionUrl) {
      const url = String(
        notification.actionUrl
      ).trim();

      if (url.startsWith("/")) {
        navigate(url);
      } else {
        window.location.href = url;
      }

      return;
    }

    /*
      If notification has a symbol,
      open the market symbol page.
    */

    if (notification?.symbol) {
      navigate(
        `/markets/${encodeURIComponent(
          notification.symbol
        )}`
      );

      return;
    }
  };


  /* =======================================================
     TAB CHANGE
  ======================================================= */

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };


  /* =======================================================
     BACK
  ======================================================= */

  const handleBack = () => {
    navigate(-1);
  };


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="notification-page">
        <div className="notification-loading">
          <div className="notification-spinner" />

          <span>
            Loading notifications...
          </span>
        </div>
      </div>
    );
  }


  /* =======================================================
     UI
  ======================================================= */

  return (
    <div className="notification-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <header className="notification-header">
        <button
          type="button"
          className="notification-back"
          onClick={handleBack}
          aria-label="Go back"
        >
          <span>‹</span>
        </button>

        <h1>
          Notification centre
        </h1>
      </header>


      {/* =================================================
          TABS
      ================================================= */}

      <nav className="notification-tabs">

        {/* MARKETS */}

        <button
          type="button"
          className={
            activeTab === "markets"
              ? "notification-tab active"
              : "notification-tab"
          }
          onClick={() =>
            handleTabChange("markets")
          }
        >
          <span>
            Markets
          </span>

          {tabCounts.markets > 0 && (
            <span className="notification-badge">
              {tabCounts.markets > 99
                ? "99+"
                : tabCounts.markets}
            </span>
          )}
        </button>


        {/* MESSAGES */}

        <button
          type="button"
          className={
            activeTab === "messages"
              ? "notification-tab active"
              : "notification-tab"
          }
          onClick={() =>
            handleTabChange("messages")
          }
        >
          <span>
            Messages
          </span>

          {tabCounts.messages > 0 && (
            <span className="notification-badge">
              {tabCounts.messages > 99
                ? "99+"
                : tabCounts.messages}
            </span>
          )}
        </button>


        {/* ACCOUNT */}

        <button
          type="button"
          className={
            activeTab === "account"
              ? "notification-tab active"
              : "notification-tab"
          }
          onClick={() =>
            handleTabChange("account")
          }
        >
          <span>
            Account
          </span>

          {tabCounts.account > 0 && (
            <span className="notification-badge">
              {tabCounts.account > 99
                ? "99+"
                : tabCounts.account}
            </span>
          )}
        </button>


        {/* HF NEWS */}

        <button
          type="button"
          className={
            activeTab === "news"
              ? "notification-tab active"
              : "notification-tab"
          }
          onClick={() =>
            handleTabChange("news")
          }
        >
          <span>
            BFC News
          </span>

          {tabCounts.news > 0 && (
            <span className="notification-badge">
              {tabCounts.news > 99
                ? "99+"
                : tabCounts.news}
            </span>
          )}
        </button>

      </nav>


      {/* =================================================
          CONTENT
      ================================================= */}

      <main className="notification-content">

        {filteredNotifications.length === 0 ? (
          <div className="notification-empty">
            <div className="notification-empty-icon">
              🔔
            </div>

            <strong>
              No notifications
            </strong>

            <span>
              Hakuna ujumbe wowote kwa sasa.
            </span>
          </div>
        ) : (
          filteredNotifications.map((notification) => {
            const category = getCategory(
              notification
            );

            const title = getTitle(
              notification
            );

            const message = getMessage(
              notification
            );

            const actionText = getActionText(
              notification
            );

            return (
              <article
                key={notification.id}
                className={`notification-card ${category}`}
              >

                {/* CARD TITLE */}

                <div className="notification-card-title">
                  <h2>
                    {title}
                  </h2>
                </div>


                {/* CARD BODY */}

                <div className="notification-card-body">

                  {message && (
                    <div className="notification-message">
                      {message}
                    </div>
                  )}


                  {/* DATE AND ACTION */}

                  <div className="notification-card-footer">

                    <time>
                      {formatDate(notification)}
                    </time>

                    {actionText && (
                      <button
                        type="button"
                        className="notification-action"
                        onClick={() =>
                          handleAction(notification)
                        }
                      >
                        {actionText}
                      </button>
                    )}

                  </div>

                </div>

              </article>
            );
          })
        )}

      </main>
    </div>
  );
};

export default Notifications;